import { Alert } from '@apollo/orbit';
import React from 'react';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { Loading } from 'src/components/common/loading/Loading';
import { NoData } from 'src/components/common/noData/NoData';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphqlCodeBlock } from 'src/components/graphqlCodeBlock/GraphqlCodeBlock';
import { DocsRouter } from 'src/lib/routers';
import {
  isOperationUnexecutable,
  unexecutableOperationReason,
} from 'src/lib/unexecutableOperationReason';

type OperationSignatureProps = {
  signature?: string;
  name: string;
  loading: boolean;
  error?: boolean;
};

export function OperationSignature({
  name,
  loading,
  error,
  signature,
}: OperationSignatureProps) {
  return (
    <>
      {error ? (
        <ErrorMessage>Error fetching operation signature</ErrorMessage>
      ) : loading ? (
        <Loading />
      ) : (
        <>
          {name && isOperationUnexecutable(name) ? (
            <NoData
              message="Unexecutable Operation"
              secondary={
                <>
                  {unexecutableOperationReason(name)}
                  <Alert status="info">
                    By default, operation signatures are not reported for
                    invalid operations. If you are using Apollo Server, you can{' '}
                    <ClickableText
                      className="font-semibold underline"
                      as="a"
                      rel="noopener noreferrer"
                      target="_blank"
                      href={DocsRouter.path('UsageReportingPluginWithHash', {
                        hash: 'sendunexecutableoperationdocuments',
                      })}
                    >
                      configure the usage reporting plugin to report the full
                      operation document and operation name
                    </ClickableText>{' '}
                    so you can view it in Apollo Studio's trace view.
                  </Alert>
                </>
              }
            />
          ) : signature ? (
            <ErrorBoundary
              // if we catch an error rendering the GraphQL codeblock due to parse failure,
              // fall back to a simple text renderer.
              renderError={() => (
                <>
                  <Alert status="warning">
                    There was a problem parsing this operation signature. Check
                    for GraphQL syntax errors in the operation below, or open in
                    Explorer for additional formatting hints.
                  </Alert>
                  <div className="font-mono text-sm">{signature}</div>
                </>
              )}
            >
              <GraphqlCodeBlock sdl={signature} />
            </ErrorBoundary>
          ) : (
            <NoData
              message="No Signature"
              secondary="Uh oh! It looks like your query is missing a query signature."
            />
          )}
        </>
      )}
    </>
  );
}
